import { useCallback, useId, useMemo, useState } from 'react';
import { useI18n } from '../i18n';
import type { MessageKey } from '../i18n/messages';
import type { GymHealthDeclarationFormState, GymHealthDeclarationGender } from '../types/gymHealthDeclaration';
import {
  emptyGymHealthDeclarationForm,
  GYM_HEALTH_DECLARATION_BOOL_KEYS,
} from '../types/gymHealthDeclaration';
import {
  buildGymHealthDeclarationWirePayload,
  gymHealthDeclarationFormIsComplete,
} from '../utils/gymHealthDeclaration';
import styles from './GymHealthDeclarationPanel.module.css';

type GymHealthDeclarationPanelProps = {
  onSubmit: (payload: ReturnType<typeof buildGymHealthDeclarationWirePayload>) => Promise<void>;
  onCancel: () => void;
};

const BOOL_QUESTION_I18N: Record<(typeof GYM_HEALTH_DECLARATION_BOOL_KEYS)[number], MessageKey> = {
  heart_disease: 'gymHealth.q.heartDisease',
  chest_pain_at_rest: 'gymHealth.q.chestPainRest',
  chest_pain_daily_activity: 'gymHealth.q.chestPainDaily',
  chest_pain_exercise: 'gymHealth.q.chestPainExercise',
  dizziness_balance_loss: 'gymHealth.q.dizziness',
  loss_of_consciousness: 'gymHealth.q.consciousness',
  asthma_recent_symptoms: 'gymHealth.q.asthma',
  family_heart_disease_death: 'gymHealth.q.familyHeart',
  family_sudden_early_death: 'gymHealth.q.familySuddenDeath',
  doctor_supervised_activity_only: 'gymHealth.q.doctorSupervision',
  other_chronic_limiting_condition: 'gymHealth.q.chronicOther',
  high_risk_pregnancy: 'gymHealth.q.pregnancyRisk',
  smoker: 'gymHealth.q.smoker',
};

function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

function YesNoQuestion({
  label,
  value,
  onChange,
  name,
}: {
  label: string;
  value: boolean | null;
  onChange: (next: boolean) => void;
  name: string;
}) {
  const { t } = useI18n();
  return (
    <div className={styles.questionBlock}>
      <p className={styles.questionText}>{label}</p>
      <div className={styles.optionRow} role="group" aria-label={label}>
        <button
          type="button"
          className={[styles.optionButton, value === true ? styles.optionButtonActive : ''].join(' ')}
          aria-pressed={value === true}
          onClick={() => onChange(true)}
        >
          {t('common.yes')}
        </button>
        <button
          type="button"
          className={[styles.optionButton, value === false ? styles.optionButtonActive : ''].join(' ')}
          aria-pressed={value === false}
          onClick={() => onChange(false)}
        >
          {t('common.no')}
        </button>
      </div>
      <input type="hidden" name={name} value={value === null ? '' : value ? 'yes' : 'no'} />
    </div>
  );
}

export function GymHealthDeclarationPanel({ onSubmit, onCancel }: GymHealthDeclarationPanelProps) {
  const { t, language } = useI18n();
  const titleId = useId();
  const [form, setForm] = useState<GymHealthDeclarationFormState>(emptyGymHealthDeclarationForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentYear = new Date().getFullYear();
  const years = useMemo(
    () => Array.from({ length: currentYear - 1920 + 1 }, (_, i) => currentYear - i),
    [currentYear],
  );
  const months = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => ({
        value: i + 1,
        label: new Intl.DateTimeFormat(language === 'he' ? 'he-IL' : undefined, { month: 'long' }).format(
          new Date(2000, i, 1),
        ),
      })),
    [language],
  );

  const maxDay = useMemo(() => {
    const y = Number(form.birthYear);
    const m = Number(form.birthMonth);
    if (!Number.isFinite(y) || !Number.isFinite(m) || y < 1 || m < 1) {
      return 31;
    }
    return daysInMonth(y, m);
  }, [form.birthYear, form.birthMonth]);

  const days = useMemo(() => Array.from({ length: maxDay }, (_, i) => i + 1), [maxDay]);

  const canSubmit = gymHealthDeclarationFormIsComplete(form) && !submitting;

  const setGender = useCallback((gender: GymHealthDeclarationGender) => {
    setForm((prev) => ({ ...prev, gender }));
  }, []);

  const setBoolAnswer = useCallback((key: (typeof GYM_HEALTH_DECLARATION_BOOL_KEYS)[number], value: boolean) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const onFormSubmit = useCallback(async () => {
    if (!gymHealthDeclarationFormIsComplete(form)) {
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(buildGymHealthDeclarationWirePayload(form));
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message || t('gymHealth.submitError'));
    } finally {
      setSubmitting(false);
    }
  }, [form, onSubmit, t]);

  let questionNumber = 3;

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true" aria-labelledby={titleId}>
      <div className={styles.sheet}>
        <div className={styles.headerRow}>
          <h2 className={styles.title} id={titleId}>
            {t('gymHealth.title')}
          </h2>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onCancel}
            aria-label={t('gymHealth.cancel')}
          >
            ×
          </button>
        </div>

        <p className={styles.intro}>{t('gymHealth.intro')}</p>

        <div className={styles.questionBlock}>
          <p className={styles.questionText}>{t('gymHealth.q.gender')}</p>
          <div className={styles.optionRowThree} role="group" aria-label={t('gymHealth.q.gender')}>
            {(['male', 'female', 'other'] as const).map((gender) => (
              <button
                key={gender}
                type="button"
                className={[
                  styles.optionButton,
                  form.gender === gender ? styles.optionButtonActive : '',
                ].join(' ')}
                aria-pressed={form.gender === gender}
                onClick={() => setGender(gender)}
              >
                {t(
                  gender === 'male'
                    ? 'gymHealth.gender.male'
                    : gender === 'female'
                      ? 'gymHealth.gender.female'
                      : 'gymHealth.gender.other',
                )}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.questionBlock}>
          <p className={styles.questionText}>{t('gymHealth.q.birthDate')}</p>
          <div className={styles.birthDateRow}>
            <div className={styles.birthDatePart}>
              <label className={styles.birthDateLabel} htmlFor={`${titleId}-day`}>
                {t('common.day')}
              </label>
              <select
                id={`${titleId}-day`}
                className={styles.select}
                value={form.birthDay}
                onChange={(event) => setForm((prev) => ({ ...prev, birthDay: event.target.value }))}
              >
                <option value="">{t('common.day')}</option>
                {days.map((day) => (
                  <option key={day} value={String(day)}>
                    {day}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.birthDatePart}>
              <label className={styles.birthDateLabel} htmlFor={`${titleId}-month`}>
                {t('common.month')}
              </label>
              <select
                id={`${titleId}-month`}
                className={styles.select}
                value={form.birthMonth}
                onChange={(event) => setForm((prev) => ({ ...prev, birthMonth: event.target.value }))}
              >
                <option value="">{t('common.month')}</option>
                {months.map(({ value, label }) => (
                  <option key={value} value={String(value)}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.birthDatePart}>
              <label className={styles.birthDateLabel} htmlFor={`${titleId}-year`}>
                {t('common.year')}
              </label>
              <select
                id={`${titleId}-year`}
                className={styles.select}
                value={form.birthYear}
                onChange={(event) => setForm((prev) => ({ ...prev, birthYear: event.target.value }))}
              >
                <option value="">{t('common.year')}</option>
                {years.map((year) => (
                  <option key={year} value={String(year)}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {GYM_HEALTH_DECLARATION_BOOL_KEYS.map((key) => {
          const n = questionNumber++;
          const label = `${n}. ${t(BOOL_QUESTION_I18N[key])}`;
          return (
            <YesNoQuestion
              key={key}
              name={key}
              label={label}
              value={form[key]}
              onChange={(value) => setBoolAnswer(key, value)}
            />
          );
        })}

        <h3 className={styles.sectionTitle}>{t('gymHealth.instructionsTitle')}</h3>
        <ol className={styles.instructionsList}>
          <li className={styles.instructionItem}>{t('gymHealth.instruction1')}</li>
          <li className={styles.instructionItem}>{t('gymHealth.instruction2')}</li>
        </ol>

        <h3 className={styles.sectionTitle}>{t('gymHealth.declarationTitle')}</h3>
        <p className={styles.declarationText}>{t('gymHealth.declarationBody')}</p>

        <label className={styles.agreeRow} htmlFor={`${titleId}-agree`}>
          <input
            id={`${titleId}-agree`}
            type="checkbox"
            className={styles.agreeCheckbox}
            checked={form.declaration_agreed}
            onChange={(event) =>
              setForm((prev) => ({ ...prev, declaration_agreed: event.target.checked }))
            }
          />
          <span className={styles.agreeLabel}>{t('gymHealth.declarationAgree')}</span>
        </label>

        {error ? (
          <p className={styles.error} role="alert">
            {error}
          </p>
        ) : null}

        <button type="button" className={styles.submitButton} disabled={!canSubmit} onClick={() => void onFormSubmit()}>
          {submitting ? t('common.submitting') : t('gymHealth.submit')}
        </button>
        <button type="button" className={styles.cancelButton} onClick={onCancel} disabled={submitting}>
          {t('gymHealth.cancel')}
        </button>
      </div>
    </div>
  );
}
